'use client';

import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';

import { useExtensionFlow } from '@/widgets/ExtensionFlow/context/ExtensionFlowContext';

import {
  AccessLevel,
  CtaButton,
  ExtensionFlowStepsCollection,
  Maybe,
  MembershipType,
  Overlay,
} from '@/generated/contentfulTypes';
import { QUERY_KEYS } from '@/common/types/react-query-keys.interfaces';

import { removePaymentInfo } from '@/common/api/payment-session';
import fetchBecomeAMemberCombinations from '@/common/utils/fetchBecomeAMemberCombination';
import { BecomeAMemberCombinationFetchingTypes, CombinationLabelGroups } from '@/common/types/combinations.types';
import { getAllClubs } from '@/common/utils/getAllClubs';
import { getCookie, removeCookie } from '@/common/utils/cookies';
import { decodeJwt } from '@/common/utils/jwtHelper';
import { getStepTitle } from '@/common/components/SalesFlow/StepsContent/utils/getStepTitle';
import { getAllLocales } from '@/common/utils/getAllLocales';
import { CONTENTFUL_TO_FLOW } from '@/common/components/Locator/utils/flowNameMappings';
import { FlowNames } from '@/common/components/Locator/Locator.interfaces';

import { CombinationFlowTypes, CookieKeys, Languages, UrlParamKeys } from '@/common/utils/constants';
import { FlowType } from '@/common/api/interfaces';

import MemberVerification from './MemberVerification/MemberVerification';
import ExtensionFlowStepsContent from './StepsContent/StepsContent';

interface ExtensionFlowContentProps {
  lang: Languages;
  stepsCollection: Maybe<ExtensionFlowStepsCollection>;
  findClubOverlay: Maybe<Overlay>;
  findMemberNumberOverlay: Maybe<Overlay>;
  chooseNewClubCTA: Maybe<CtaButton>[];
  accessLevelOrderCollection?: Maybe<AccessLevel>[];
  membershipTypeOrderCollection?: Maybe<MembershipType>[];
}

export default function ExtensionFlowContent({
  lang,
  stepsCollection,
  findClubOverlay,
  findMemberNumberOverlay,
  chooseNewClubCTA,
  accessLevelOrderCollection,
  membershipTypeOrderCollection,
}: ExtensionFlowContentProps) {
  const { translations, isAuthorized, setIsAuthorized, setCustomerExtensionEndDate, setActiveStep } = useExtensionFlow();

  const { data: localesData } = useQuery({
    queryKey: [QUERY_KEYS.ALL_LOCALES, lang],
    queryFn: () => getAllLocales({ lang: lang as Languages }),
  });
  const countryCode = localesData?.currentCountry?.countryCode;

  const { data: clubs } = useQuery({
    queryKey: [QUERY_KEYS.ALL_CLUBS, lang],
    queryFn: () => getAllClubs({ lang: lang as Languages, countryCode: countryCode ?? undefined }),
    enabled: !!countryCode,
  });

  const searchParams = useSearchParams();
  const studioId = searchParams.get(UrlParamKeys.StudioId);

  const clubsForMemberVerification = useMemo(
    () =>
      clubs?.filter((club) => {
        const isOmittedInExtension = club.omitClubInFlows?.some(
          (contentfulFlow) =>
            !!contentfulFlow &&
            CONTENTFUL_TO_FLOW[contentfulFlow] === FlowNames.Extension
        );

        return !isOmittedInExtension;
      }) ?? [],
    [clubs]
  );

  const stepItems = stepsCollection?.items;
  const stepTitles = {
    membershipVerification: getStepTitle(0, stepItems),
    membershipType: getStepTitle(2, stepItems),
    accessLevel: getStepTitle(3, stepItems),
    terms: getStepTitle(4, stepItems),
    sms: getStepTitle(5, stepItems),
  };

  const previousEndDateRef = useRef<string | null>(null);

  useEffect(() => {
    const endDate = getCookie(CookieKeys.EXTENSION_END_DATE);

    if (endDate === previousEndDateRef.current) {
      return;
    }

    previousEndDateRef.current = endDate;

    if (endDate) {
      setCustomerExtensionEndDate(endDate);
    } else {
      setCustomerExtensionEndDate(null);
    }
  }, [setCustomerExtensionEndDate, isAuthorized]);

  const resetExtensionAuth = () => {
    removeCookie(CookieKeys.EXTENSION_AUTH_TOKEN);
    removeCookie(CookieKeys.EXTENSION_END_DATE);
    removeCookie(CookieKeys.EXTENSION_HAS_PAYMENT_METHOD);
    removeCookie(CookieKeys.EXTENSION_CUSTOMER_ID);
    removePaymentInfo(FlowType.EXTENSION);
    setIsAuthorized(false);
    if (stepTitles.membershipVerification) {
      setActiveStep(stepTitles.membershipVerification);
    }
  };

  useEffect(() => {
    if (!countryCode || !clubs) return;

    const token = getCookie(CookieKeys.EXTENSION_AUTH_TOKEN);
    if (!token) {
      setIsAuthorized(false);
      removePaymentInfo(FlowType.EXTENSION);
      if (stepTitles.membershipVerification) {
        setActiveStep(stepTitles.membershipVerification);
      }
      return;
    }


    const payload = decodeJwt(token);
    if (!payload?.exp) {
      setIsAuthorized(false);
      removePaymentInfo(FlowType.EXTENSION);
      if (stepTitles.membershipVerification) {
        setActiveStep(stepTitles.membershipVerification);
      }
      return;
    }

    const now = Math.floor(Date.now() / 1000);
    const isValid = payload.exp > now;
    const isStudioMatch = Number(payload.venue_id) === Number(studioId);

    const selectedClub = clubsForMemberVerification.find(
      (club) => club.clubId === studioId
    );
    const selectedClubCountry = selectedClub?.address.countryCodeAlpha2?.toUpperCase();
    const localeCountry = countryCode.toUpperCase();

    const isLocaleMatch = !!selectedClubCountry && selectedClubCountry === localeCountry;

    if (isValid && isStudioMatch && isLocaleMatch) {
      setIsAuthorized(true);
    } else {
      resetExtensionAuth();
    }
  }, [studioId, countryCode, clubs, clubsForMemberVerification]);

  const { data: combinationsData, isFetching: isFetchingCombinations } =
    useQuery<CombinationLabelGroups, [BecomeAMemberCombinationFetchingTypes]>({
      queryKey: [QUERY_KEYS.COMBINATIONS, lang, studioId],
      queryFn: () =>
        fetchBecomeAMemberCombinations({
          lang: lang,
          id: studioId as string,
          accessLevelOrderCollection: accessLevelOrderCollection,
          membershipTypeOrderCollection: membershipTypeOrderCollection,
          flow: CombinationFlowTypes.ShowInExtensionFlow,
        }),
      enabled: !!studioId && isAuthorized === true,
    });
  const selectedClub = clubsForMemberVerification.find(
    (club) => club.clubId === studioId
  );

  if (!isAuthorized) {
    return clubs && (<MemberVerification
      clubs={clubsForMemberVerification}
      step={stepTitles.membershipVerification}
      translations={translations}
      findClubOverlay={findClubOverlay}
      findMemberNumberOverlay={findMemberNumberOverlay}
      chooseNewClubCTA={chooseNewClubCTA}
      onVerificationSuccess={() => setIsAuthorized(true)}
      preselectedClub={selectedClub}
    />)
  }

  return (
    <>
      {studioId && (
        <ExtensionFlowStepsContent
          studioId={studioId}
          stepsCollection={stepsCollection as Maybe<ExtensionFlowStepsCollection>}
          combinationsData={combinationsData}
          lang={lang}
          fetchingCombinationComplete={!isFetchingCombinations}
          isExtending={true}
          translations={translations}
          club={selectedClub}
        />
      )}
    </>
  );
}
